import type { RequestTypeDto, TemplateDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { PdfView } from '../components/pdf-view';
import { api, ApiError } from '../lib/api';
import { Icon } from '../lib/icons';
import { formatDate, useToast } from '../lib/ui';

export function TemplatesPage() {
  const templates = useQuery({ queryKey: ['templates', 'all'], queryFn: () => api.get<TemplateDto[]>('/templates', { all: true }) });
  const [editing, setEditing] = useState<TemplateDto | 'new' | null>(null);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Шаблоны документов</h1>
          <p className="muted">
            Файлы Word и Excel с метками <code>{'{{поле}}'}</code> — система заполнит их данными заявки
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          <Icon name="plus" size={18} /> Загрузить шаблон
        </button>
      </div>
      <div className="route-cards">
        {templates.data?.map((t) => (
          <button key={t.id} className="card route-card" onClick={() => setEditing(t)}>
            <div className="section-head">
              <b>{t.name}</b>
              <span style={{ display: 'flex', gap: 6 }}>
                <span className={`badge ${t.format === 'docx' ? 'badge-info' : 'badge-brand'}`}>.{t.format}</span>
                {!t.isActive && <span className="badge badge-danger">отключён</span>}
              </span>
            </div>
            <span className="faint">
              {t.outputKind === 'document' ? 'Основной документ задачи' : 'Сформированный документ'} · меток: {t.placeholders.length} · версия {t.version} ·{' '}
              {formatDate(t.updatedAt)}
            </span>
          </button>
        ))}
      </div>
      {templates.data?.length === 0 && (
        <div className="card card-pad muted">
          Пример: загрузите приказ о командировке, где вместо ФИО написано <code>{'{{employee}}'}</code>, а вместо дат — <code>{'{{dateFrom}}'}</code> и{' '}
          <code>{'{{dateTo}}'}</code>. Для строк расписания: <code>{'{{#schedule}}{{date}} {{description}}{{/schedule}}'}</code>.
        </div>
      )}
      {editing && <TemplateEditor template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function TemplateEditor({ template, onClose }: { template: TemplateDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [outputName, setOutputName] = useState(template?.outputName ?? '');
  const [outputKind, setOutputKind] = useState<'document' | 'generated'>(template?.outputKind ?? 'generated');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [picked, setPicked] = useState<File | null>(null);
  const [current, setCurrent] = useState<TemplateDto | null>(template);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.set('name', name);
      form.set('description', description);
      form.set('outputName', outputName || name);
      form.set('outputKind', outputKind);
      form.set('isActive', String(isActive));
      if (picked) form.set('file', picked);
      const res = await fetch(current ? `/api/templates/${current.id}` : '/api/templates', { method: current ? 'PATCH' : 'POST', body: form, credentials: 'same-origin' });
      const data = (await res.json()) as TemplateDto & { message?: string };
      if (!res.ok) throw new ApiError(res.status, data.message ?? 'Не удалось сохранить шаблон');
      return data;
    },
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      setCurrent(t);
      setPicked(null);
      toast(`Шаблон сохранён, найдено меток: ${t.placeholders.length}. Проверьте его на примере`);
    },
    onError: (e) => toast(e.message, { error: true }),
  });

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <form
        className="drawer drawer-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Шаблон"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <header>
          <h2>{current ? current.name : 'Новый шаблон'}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" />
          </button>
        </header>
        <div className="body">
          <label className="field">
            <span>Название</span>
            <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>Имя файла результата</span>
            <input className="input" placeholder="Приказ о командировке №{{number}}" value={outputName} onChange={(e) => setOutputName(e.target.value)} />
            <span className="faint">Можно с метками: {'{{number}}'}, {'{{employee}}'}…</span>
          </label>
          <label className="field">
            <span>Что получается</span>
            <select className="select" value={outputKind} onChange={(e) => setOutputKind(e.target.value as 'document' | 'generated')}>
              <option value="generated">Сформированный документ (приказ, смета, заявление)</option>
              <option value="document">Основной документ задачи — его правят и согласуют (договор)</option>
            </select>
          </label>
          <label className="field">
            <span>Описание</span>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>Используется</span>
          </label>
          <div className="dropzone">
            <button type="button" className="btn" onClick={() => file.current?.click()}>
              {current ? 'Заменить файл' : 'Выбрать файл .docx или .xlsx'}
            </button>
            <span className="faint">{picked ? picked.name : current ? `${current.originalName}, версия ${current.version}` : 'файл не выбран'}</span>
            <input ref={file} type="file" accept=".docx,.xlsx" hidden onChange={(e) => setPicked(e.target.files?.[0] ?? null)} />
          </div>
          {current && <TemplateCheck template={current} />}
        </div>
        <footer>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Закрыть
          </button>
          <button className="btn btn-primary" disabled={save.isPending || !name.trim() || (!current && !picked)}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </footer>
      </form>
    </>
  );
}

/** Проверка на примере: какие метки заполнятся для выбранного типа заявки и как выглядит результат. */
function TemplateCheck({ template }: { template: TemplateDto }) {
  const types = useQuery({ queryKey: ['request-types', 'all'], queryFn: () => api.get<RequestTypeDto[]>('/request-types', { all: true }) });
  const [typeId, setTypeId] = useState('');
  const [preview, setPreview] = useState(false);
  const check = useQuery({
    queryKey: ['templates', template.id, 'check', typeId, template.version],
    queryFn: () =>
      api.get<{ placeholders: string[]; unknown: string[]; suggestions: Record<string, string> }>(`/templates/${template.id}/check`, { requestTypeId: typeId || undefined }),
  });
  const unknown = new Set(check.data?.unknown ?? []);
  const sampleUrl = `/api/templates/${template.id}/sample?requestTypeId=${typeId}&v=${template.version}`;
  return (
    <fieldset>
      <legend>Проверка на примере</legend>
      <select className="select" value={typeId} onChange={(e) => setTypeId(e.target.value)} aria-label="Тип заявки">
        <option value="">Для какого типа заявки?</option>
        {types.data?.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <div className="chain">
        {check.data?.placeholders.map((p) => (
          <span key={p} className={`chip${unknown.has(p.replace(/^#/, '')) ? ' chip-warn' : ''}`} title={unknown.has(p.replace(/^#/, '')) ? 'Такого поля нет — метка останется пустой' : 'Заполнится'}>
            {unknown.has(p.replace(/^#/, '')) ? '⚠ ' : '✓ '}
            {p}
          </span>
        ))}
        {check.data?.placeholders.length === 0 && <span className="faint">В шаблоне нет меток {'{{…}}'}</span>}
      </div>
      {unknown.size > 0 && <span className="field-error">Метки с ⚠ не найдены среди полей выбранного типа — проверьте написание или добавьте поля.</span>}
      {/* Система сама предлагает, как, скорее всего, должна называться метка. */}
      {Object.keys(check.data?.suggestions ?? {}).length > 0 && (
        <ul className="placeholder-hints">
          {Object.entries(check.data!.suggestions).map(([bad, good]) => (
            <li key={bad}>
              <code>{`{{${bad}}}`}</code> → возможно, <code>{`{{${good}}}`}</code> — исправьте в файле шаблона и загрузите его заново
            </li>
          ))}
        </ul>
      )}
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <a className="btn btn-sm" href={sampleUrl}>
          Скачать пример
        </a>
        <button type="button" className="btn btn-sm" onClick={() => setPreview(true)}>
          Посмотреть пример
        </button>
      </span>
      {preview && (
        <div className="viewer" role="dialog" aria-modal="true" aria-label="Пример документа">
          <header>
            <b>{template.name}</b>
            <span className="faint">пример с тестовыми данными</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost btn-icon" onClick={() => setPreview(false)} aria-label="Закрыть">
              <Icon name="close" />
            </button>
          </header>
          <div className="viewer-body">
            <PdfView url={`${sampleUrl}&pdf=true`} />
          </div>
        </div>
      )}
    </fieldset>
  );
}
